<template>
  <div class="qvp-kids-exam-center">
    <div class="page-header">
      <p class="page-subtitle mal-text">മദ്റസ തിരഞ്ഞെടുത്ത് പരീക്ഷാ കേന്ദ്ര വിവരങ്ങൾ നൽകുക</p>
    </div>

    <!-- District admin: pick zone -->
    <div v-if="isDistrictAdmin" class="form-group basic">
      <div class="input-wrapper">
        <label class="label mal-text">സോൺ (Zone) <span class="text-danger">*</span></label>
        <select v-model="selectedZoneId" class="form-control form-select">
          <option value="" disabled class="mal-text">സോൺ തിരഞ്ഞെടുക്കുക</option>
          <option v-for="z in zones" :key="'z-'+z.id" :value="String(z.id)">{{ z.name }}</option>
        </select>
        <div class="text-muted small mt-1 mal-text" v-if="zonesLoading">സോണുകൾ ലോഡ് ചെയ്യുന്നു...</div>
        <div class="text-muted small mt-1 mal-text" v-else-if="zones.length === 0">സോൺ ലിസ്റ്റ് കണ്ടെത്തിയില്ല.</div>
      </div>
    </div>

    <!-- Add button -->
    <div v-if="canAddCenter && effectiveZoneId" class="add-btn-wrap">
      <button type="button" class="btn btn-primary btn-block add-btn" @click="openAddModal">
        <ion-icon name="add-outline"></ion-icon>
        <span class="mal-text">ചേർക്കുക (Add)</span>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <!-- Stored centers list -->
    <div v-else-if="savedCenters.length > 0" class="saved-centers-section">
      <h4 class="section-title mal-text">പരീക്ഷാ കേന്ദ്രങ്ങൾ</h4>
      <div class="saved-centers-list">
        <div v-for="(center, idx) in savedCenters" :key="center.id || idx" class="saved-center-card">
          <div class="center-info">
            <strong class="center-name">{{ getCenterName(center) }}</strong>
            <span class="center-detail">കൺവീനർ: {{ center.convenor_name }}</span>
            <span class="convenor-phone">
              <a v-if="center.convenor_phone" :href="'tel:' + center.convenor_phone">{{ center.convenor_phone }}</a>
              <template v-else>{{ center.convenor_phone || '-' }}</template>
            </span>
          </div>
          <div class="center-actions">
            <button type="button" class="btn btn-sm btn-outline-primary" @click="openEditModal(center)">എഡിറ്റ്</button>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="confirmDelete(center)">ഡിലീറ്റ്</button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="empty-note mal-text">ഇതുവരെ പരീക്ഷാ കേന്ദ്രങ്ങൾ ചേർത്തിട്ടില്ല.</div>

    <!-- Add/Edit Modal (bottom sheet) -->
    <div v-if="formModalOpen" class="modal-overlay action-sheet-open" @click.self="closeFormModal">
      <div class="modal-sheet">
        <div class="modal-sheet-handle"></div>
        <h5 class="modal-title mal-text">{{ editingId ? 'പരീക്ഷാ കേന്ദ്രം എഡിറ്റ് ചെയ്യുക' : 'പരീക്ഷാ കേന്ദ്രം ചേർക്കുക' }}</h5>
        <form @submit.prevent="submitFormModal">
          <div class="form-group">
            <label class="form-label mal-text">മദ്റസ (Madrasa) <span class="text-danger">*</span></label>
            <select v-if="!useTextInputForMadrasa" v-model="form.madrasa_id" class="form-control" required>
              <option value="" disabled class="mal-text">മദ്റസ തിരഞ്ഞെടുക്കുക</option>
              <option v-for="m in availableMadrasasForSelection" :key="m.id" :value="m.id">{{ m.name }}</option>
              <option value="__other__" class="mal-text">മറ്റുള്ളവ (Other)</option>
            </select>
            <template v-else>
              <input v-model="form.madrasa_name" type="text" class="form-control" :placeholder="madrasaInputPlaceholder" required />
              <small v-if="form.madrasa_id === '__other__'" class="text-muted mal-text d-block mt-1">
                പരീക്ഷാ കേന്ദ്രത്തിന്റെ പേര് ടൈപ്പ് ചെയ്യുക.
              </small>
              <small v-else-if="availableMadrasasForSelection.length === 0 && hasMadrasas" class="text-muted mal-text d-block mt-1">
                എല്ലാ മദ്റസകളും ചേർത്തിട്ടുണ്ട്. പുതിയ കേന്ദ്രത്തിന്റെ പേര് ടൈപ്പ് ചെയ്യുക.
              </small>
            </template>
          </div>
          <div class="form-group">
            <label class="form-label mal-text">കൺവീനർ പേര് (Convenor Name) <span class="text-danger">*</span></label>
            <input v-model="form.convenor_name" type="text" class="form-control" placeholder="കൺവീനർ പേര്" required />
          </div>
          <div class="form-group">
            <label class="form-label mal-text">കൺവീനർ ഫോൺ നമ്പർ (Convenor Phone) <span class="text-danger">*</span></label>
            <input v-model="form.convenor_phone" type="tel" class="form-control" placeholder="10 അക്ക മൊബൈൽ നമ്പർ" maxlength="10" inputmode="numeric" required />
          </div>
          <div class="form-group">
            <label class="form-label mal-text">Exam Date</label>
            <input v-model="form.exam_date" type="date" class="form-control" />
          </div>
          <div class="form-group">
            <label class="form-label mal-text">Exam Time</label>
            <input v-model="form.exam_time" type="time" class="form-control" />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeFormModal">ക്യാൻസൽ</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'സേവ് ചെയ്യുന്നു...' : 'സേവ് ചെയ്യുക' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete confirm modal -->
    <div v-if="deleteModalOpen" class="modal-overlay" @click.self="deleteModalOpen = false">
      <div class="modal-box modal-confirm">
        <h5 class="modal-title mal-text">ഡിലീറ്റ് ചെയ്യണോ?</h5>
        <p class="mal-text" v-if="centerToDelete">{{ getCenterName(centerToDelete) }} - ഈ പരീക്ഷാ കേന്ദ്രം ഡിലീറ്റ് ചെയ്യുമോ?</p>
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click="deleteModalOpen = false">ക്യാൻസൽ</button>
          <button type="button" class="btn btn-danger" :disabled="deleteSaving" @click="submitDelete">{{ deleteSaving ? 'ഡിലീറ്റ് ചെയ്യുന്നു...' : 'ഡിലീറ്റ്' }}</button>
        </div>
      </div>
    </div>

    <!-- Alert Success/Error Action Sheet (slides from bottom) -->
    <div class="modal fade action-sheet" id="actionSheetAlert" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <div class="action-sheet-content">
              <div class="iconbox text-success" v-if="alertClass === 'alert-success'">
                <ion-icon name="checkmark-circle"></ion-icon>
              </div>
              <div class="iconbox text-danger" v-if="alertClass === 'alert-error'">
                <ion-icon name="close-circle"></ion-icon>
              </div>
              <div class="text-center p-2">
                <h3>{{ alertClass === 'alert-success' ? ($t('success') || 'Success') : ($t('error') || 'Error') }}</h3>
                <p class="mal-text text-primary">{{ alertMessage }}</p>
              </div>
              <a href="javascript:void(0)" class="btn btn-primary btn-lg btn-block" @click="closeAlertModal">
                {{ $t('close') || 'Close' }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from '../services/user.service';
import $ from 'jquery';

export default {
  name: 'QVPKidsExamCenter',
  data() {
    return {
      loading: false,
      saving: false,
      deleteSaving: false,
      madrasas: [],
      savedCenters: [],
      zonesLoading: false,
      zones: [],
      selectedZoneId: '',
      formModalOpen: false,
      deleteModalOpen: false,
      editingId: null,
      centerToDelete: null,
      form: {
        madrasa_id: '',
        madrasa_name: '',
        convenor_name: '',
        convenor_phone: '',
        exam_date: '',
        exam_time: '',
      },
      alertMessage: '',
      alertClass: 'alert-success',
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    subLevel() {
      const raw = this.$route.params.sub_level;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? 0 : n;
    },
    routeId() {
      const id = this.$route.params.id;
      if (id == null || id === '') return null;
      const n = parseInt(String(id), 10);
      return Number.isNaN(n) ? null : n;
    },
    districtId() {
      // district context: route id when entering from /org/2/:id/*
      if (this.subLevel === 2 && this.routeId) return this.routeId;
      return this.currentUser?.district_id || null;
    },
    userLevel() {
      const raw = this.currentUser?.user_level;
      if (raw == null) return 0;
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? 0 : n;
    },
    isDistrictAdmin() {
      return this.userLevel == 2;
    },
    isZoneAdmin() {
      return this.userLevel == 3;
    },
    isStateOrDistrictAdmin() {
      return this.userLevel == 1 || this.userLevel == 2;
    },
    canAddCenter() {
      return this.userLevel == 1 || this.userLevel == 2 || this.userLevel == 3;
    },
    effectiveZoneId() {
      // Zone admin: zone is fixed by route/currentUser
      if (this.isZoneAdmin) {
        if (this.subLevel === 3 && this.routeId) return this.routeId;
        const z = this.currentUser?.zone_id;
        const n = z != null ? parseInt(String(z), 10) : null;
        return n && !Number.isNaN(n) ? n : null;
      }
      // District admin: must pick zone
      if (this.isDistrictAdmin) {
        const n = parseInt(String(this.selectedZoneId || ''), 10);
        return Number.isNaN(n) ? null : n;
      }
      // State admin: no zone context here
      return null;
    },
    madrasasInZone() {
      if (!this.effectiveZoneId) return [];
      const zid = this.effectiveZoneId;
      return this.madrasas.filter((m) => {
        const mz = m.zone_id != null ? parseInt(String(m.zone_id), 10) : null;
        return mz === zid || m.zone_id == zid;
      });
    },
    hasMadrasas() {
      return this.madrasasInZone.length > 0;
    },
    // Madrasas not yet added as centers (exclude already-added)
    availableMadrasasForSelection() {
      const addedIds = new Set(
        this.savedCenters
          .map((c) => c.madrasa_id)
          .filter((id) => id != null && id !== '')
          .map(String)
      );
      const addedNames = new Set(
        this.savedCenters.map((c) => (this.getCenterName(c) || '').toLowerCase().trim()).filter(Boolean)
      );
      return this.madrasasInZone.filter((m) => {
        if (addedIds.has(String(m.id))) return false;
        const name = (m.name || '').toLowerCase().trim();
        return !addedNames.has(name);
      });
    },
    // Use text input when "Other" is selected, when editing, or when all API madrasas are added (none left to select)
    useTextInputForMadrasa() {
      if (this.form.madrasa_id === '__other__') return true;
      if (this.editingId) return true;
      return this.availableMadrasasForSelection.length === 0;
    },
    madrasaInputPlaceholder() {
      if (this.form.madrasa_id === '__other__') return 'പരീക്ഷാ കേന്ദ്രത്തിന്റെ പേര് (Center name)';
      return 'മദ്റസയുടെ പേര്';
    },
  },
  watch: {
    'form.madrasa_id'(val) {
      if (val !== '__other__') this.form.madrasa_name = '';
    },
    selectedZoneId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchMadrasas();
        this.fetchSavedCenters();
      }
    },
    '$route.params.id'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.bootstrapForRole();
      }
    },
    '$route.params.sub_level'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.bootstrapForRole();
      }
    },
  },
  mounted() {
    this.$root.updateParent('QVP Kids പരീക്ഷാ കേന്ദ്രം');
    this.bootstrapForRole();
  },
  methods: {
    async bootstrapForRole() {
      // Zone admin: load directly
      if (this.isZoneAdmin && this.effectiveZoneId) {
        await Promise.all([this.fetchMadrasas(), this.fetchSavedCenters()]);
        return;
      }

      // District admin: load zones first (then wait for selection)
      if (this.isDistrictAdmin && this.districtId) {
        await this.fetchZonesForDistrict();
        // keep previous selection if still valid
        if (this.selectedZoneId) {
          const ok = this.zones.some((z) => String(z.id) === String(this.selectedZoneId));
          if (!ok) this.selectedZoneId = '';
        }
        // If only one zone, auto-select
        if (!this.selectedZoneId && this.zones.length === 1) {
          this.selectedZoneId = String(this.zones[0].id);
        }
        // When selectedZoneId changes watcher will load centers.
        if (this.effectiveZoneId) {
          await Promise.all([this.fetchMadrasas(), this.fetchSavedCenters()]);
        } else {
          this.savedCenters = [];
          this.madrasas = [];
        }
        return;
      }

      // State admin: no-op (view not supported without zone)
      this.savedCenters = [];
      this.madrasas = [];
    },
    async fetchZonesForDistrict() {
      if (!this.districtId) return;
      this.zonesLoading = true;
      this.zones = [];
      try {
        const res = await UserService.getSubLevels('get-sub-levels', { sub_level_type: 3, id: this.districtId });
        this.zones = res.data?.data?.list || [];
      } catch (e) {
        this.zones = [];
      } finally {
        this.zonesLoading = false;
      }
    },
    async fetchMadrasas() {
      if (!this.effectiveZoneId) return;
      try {
        const data = { zone_id: this.effectiveZoneId };
        if (this.districtId) data.district_id = this.districtId;
        const response = await UserService.authGetRequest('madrassas', data);
        if (response.data.status === 'success') {
          this.madrasas = response.data.data?.list || [];
        } else {
          this.showAlert(response.data.message || 'മദ്റസകൾ ലോഡ് ചെയ്യുന്നതിൽ പിശക്', 'error');
        }
      } catch (err) {
        console.error('Error fetching madrasas:', err);
        this.showAlert('മദ്റസകൾ ലോഡ് ചെയ്യുന്നതിൽ പിശക്', 'error');
      }
    },
    async fetchSavedCenters() {
      if (!this.effectiveZoneId) return;
      try {
        this.loading = true;
        const response = await UserService.authGetRequest('qvp-kids-exam-centers', { zone_id: this.effectiveZoneId });
        if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
          this.savedCenters = response.data.data.list;
        } else {
          this.savedCenters = [];
        }
      } catch (err) {
        console.warn('Could not load saved centers:', err);
        this.savedCenters = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d + 'T00:00:00');
      if (isNaN(dt.getTime())) return d;
      const day = String(dt.getDate()).padStart(2, '0');
      const month = String(dt.getMonth() + 1).padStart(2, '0');
      const year = dt.getFullYear();
      return `${day}-${month}-${year}`;
    },
    getCenterName(center) {
      if (center.madrasa_name) return center.madrasa_name;
      if (center.madrasa && center.madrasa.name) return center.madrasa.name;
      const m = this.madrasas.find((x) => x.id === center.madrasa_id || x.id == center.madrasa_id);
      return (m && m.name) ? m.name : (center.convenor_name || '');
    },
    openAddModal() {
      this.editingId = null;
      this.form = { madrasa_id: '', madrasa_name: '', convenor_name: '', convenor_phone: '', exam_date: '', exam_time: '' };
      this.formModalOpen = true;
    },
    openEditModal(center) {
      this.editingId = center.id;
      this.form = {
        madrasa_id: center.madrasa_id || '',
        madrasa_name: this.getCenterName(center),
        convenor_name: center.convenor_name || '',
        convenor_phone: (center.convenor_phone || '').replace(/\D/g, ''),
        exam_date: center.exam_date || '',
        exam_time: center.exam_time ? (String(center.exam_time).length > 5 ? String(center.exam_time).substring(0, 5) : center.exam_time) : '',
      };
      this.formModalOpen = true;
    },
    closeFormModal() {
      this.formModalOpen = false;
      this.editingId = null;
    },
    showAlert(msg, type = 'success') {
      this.alertMessage = msg;
      this.alertClass = type === 'error' ? 'alert-error' : 'alert-success';
      this.$nextTick(() => {
        $('#actionSheetAlert').modal('show');
      });
    },
    closeAlertModal() {
      $('#actionSheetAlert').modal('hide');
      this.alertMessage = '';
      this.alertClass = 'alert-success';
    },
    validatePhone(phone) {
      const p = String(phone || '').replace(/\D/g, '');
      return p.length === 10;
    },
    async submitFormModal() {
      if (!this.canAddCenter) {
        this.showAlert('ഈ നടപടി അഡ്മിനുകൾക്ക് മാത്രം സാധ്യമാണ്', 'error');
        return;
      }
      const hasMadrasaSelection = this.useTextInputForMadrasa
        ? !!this.form.madrasa_name?.trim()
        : !!this.form.madrasa_id && this.form.madrasa_id !== '__other__';
      if (!hasMadrasaSelection || !this.form.convenor_name || !this.form.convenor_phone) {
        this.showAlert('എല്ലാ ഫീൽഡുകളും പൂരിപ്പിക്കുക', 'error');
        return;
      }
      if (!this.validatePhone(this.form.convenor_phone)) {
        this.showAlert('സാധുവായ 10 അക്ക ഫോൺ നമ്പർ നൽകുക', 'error');
        return;
      }
      if (!this.effectiveZoneId) {
        this.showAlert('സോൺ വിവരം കണ്ടെത്തിയില്ല', 'error');
        return;
      }
      try {
        this.saving = true;
        let madrasaName = this.form.madrasa_name ? this.form.madrasa_name.trim() : null;
        const isOtherCenter = this.form.madrasa_id === '__other__';
        if (!madrasaName && this.form.madrasa_id && !isOtherCenter && !this.useTextInputForMadrasa) {
          const m = this.availableMadrasasForSelection.find(
            (x) => x.id == this.form.madrasa_id || x.id === this.form.madrasa_id
          ) || this.madrasasInZone.find((x) => x.id == this.form.madrasa_id || x.id === this.form.madrasa_id);
          madrasaName = m && m.name ? m.name : null;
        }

        if (this.editingId) {
          const response = await UserService.authPostRequest(`qvp-kids-exam-center/${this.editingId}`, {
            madrasa_id: null,
            madrasa_name: madrasaName,
            convenor_name: this.form.convenor_name?.trim(),
            convenor_phone: this.form.convenor_phone.trim().replace(/\D/g, ''),
            exam_date: this.form.exam_date || null,
            exam_time: this.form.exam_time || null,
          });
          if (response.data.status === 'success') {
            this.showAlert('വിജയകരമായി അപ്ഡേറ്റ് ചെയ്തു');
            this.closeFormModal();
            this.fetchSavedCenters();
          } else {
            this.showAlert(response.data.message || 'അപ്ഡേറ്റ് ചെയ്യുന്നതിൽ പിശക്', 'error');
          }
        } else {
          const payload = {
            madrasa_id: (this.form.madrasa_id && this.form.madrasa_id !== '__other__') ? parseInt(this.form.madrasa_id, 10) : null,
            madrasa_name: madrasaName,
            convenor_name: this.form.convenor_name?.trim(),
            name: this.form.convenor_name?.trim(),
            convenor_phone: this.form.convenor_phone.trim().replace(/\D/g, ''),
            exam_date: this.form.exam_date || null,
            exam_time: this.form.exam_time || null,
            zone_id: this.effectiveZoneId,
          };
          const response = await UserService.authPostRequest('qvp-kids-exam-center/store', payload);
          if (response.data.status === 'success') {
            this.showAlert('വിജയകരമായി സേവ് ചെയ്തു');
            this.closeFormModal();
            this.fetchSavedCenters();
          } else {
            this.showAlert(response.data.message || 'സേവ് ചെയ്യുന്നതിൽ പിശക്', 'error');
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'സേവ് ചെയ്യുന്നതിൽ പിശക്';
        this.showAlert(msg, 'error');
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(center) {
      this.centerToDelete = center;
      this.deleteModalOpen = true;
    },
    async submitDelete() {
      if (!this.centerToDelete) return;
      try {
        this.deleteSaving = true;
        const response = await UserService.authPostRequest(`qvp-kids-exam-center-delete/${this.centerToDelete.id}`, {});
        if (response.data.status === 'success') {
          this.showAlert('വിജയകരമായി ഡിലീറ്റ് ചെയ്തു');
          this.deleteModalOpen = false;
          this.centerToDelete = null;
          this.fetchSavedCenters();
        } else {
          this.showAlert(response.data.message || 'ഡിലീറ്റ് ചെയ്യുന്നതിൽ പിശക്', 'error');
        }
      } catch (err) {
        this.showAlert(err.response?.data?.message || 'ഡിലീറ്റ് ചെയ്യുന്നതിൽ പിശക്', 'error');
      } finally {
        this.deleteSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.qvp-kids-exam-center {
  max-width: 480px;
  margin: 0 auto;
  padding: 16px;
}
.page-header {
  margin-bottom: 20px;
}
.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a418c;
  margin-bottom: 4px;
}
.page-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
}
.exam-center-form-card {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.form-group {
  margin-bottom: 16px;
}
.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
}
.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.form-actions {
  margin-top: 20px;
}
.btn-block {
  width: 100%;
  padding: 12px;
}
.add-btn-wrap {
  margin-bottom: 16px;
}
.add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
}
.add-btn ion-icon {
  font-size: 1.25rem;
}
.saved-centers-section {
  margin-top: 8px;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1a418c;
}
.saved-centers-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.saved-center-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.center-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.center-info .center-name {
  font-size: 1rem;
  color: #1a418c;
}
.center-info .center-detail {
  font-size: 0.9rem;
  color: #6c757d;
}
.center-info .convenor-phone {
  font-size: 0.9rem;
  color: #1a418c;
}
.center-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.empty-note {
  margin-top: 18px;
  text-align: center;
  color: #6c757d;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  /* Above .footer-wrapper (App.vue), which is z-index:9999 and renders after this in the DOM. */
  z-index: 10000;
  padding: 0;
}
.modal-overlay.action-sheet-open {
  align-items: flex-end;
}
.modal-sheet {
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 20px;
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}
.modal-sheet-handle {
  width: 36px;
  height: 4px;
  background: #dee2e6;
  border-radius: 2px;
  margin: 0 auto 16px;
}
.modal-sheet .modal-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a418c;
}
.modal-sheet .form-group {
  margin-bottom: 14px;
}
.modal-sheet .modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}
.modal-box {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  max-width: 400px;
  width: 100%;
  margin: 16px;
}
.modal-box.modal-confirm .modal-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
}
.modal-box .modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
}
.modal-confirm p {
  margin-bottom: 16px;
  font-size: 0.95rem;
  color: #333;
}
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
